import { useEffect, useRef } from "react";
import { ADSENSE_PUBLISHER_ID, isValidAdSlot, isValidPublisherId } from "@/config/adsense";

const ADSENSE_SCRIPT_ID = "hustlemate-adsense-script";
const ADSENSE_SCRIPT_URL = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";

function ensureAdSenseScript(clientId) {
  const existing = document.getElementById(ADSENSE_SCRIPT_ID)
    || document.querySelector(`script[src^="${ADSENSE_SCRIPT_URL}"]`);
  if (existing) return;

  const script = document.createElement("script");
  script.id = ADSENSE_SCRIPT_ID;
  script.async = true;
  script.crossOrigin = "anonymous";
  script.src = `${ADSENSE_SCRIPT_URL}?client=${encodeURIComponent(clientId)}`;
  document.head.appendChild(script);
}

export default function AdSlot({ slot, className = "", format = "auto", fullWidthResponsive = true, onStatusChange }) {
  const adRef = useRef(null);
  const configured = isValidPublisherId(ADSENSE_PUBLISHER_ID) && isValidAdSlot(slot);

  useEffect(() => {
    if (!configured || !adRef.current) return undefined;
    let cancelled = false;

    ensureAdSenseScript(ADSENSE_PUBLISHER_ID);
    const renderAd = () => {
      if (cancelled || !adRef.current || adRef.current.dataset.adsbygoogleStatus) return;
      try {
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
      } catch {
        // Ad blockers, unavailable inventory, and offline development must not break the product UI.
      }
    };

    // The official async tag supports commands queued before or after the network script finishes loading.
    renderAd();
    return () => { cancelled = true; };
  }, [configured, slot]);

  useEffect(() => {
    if (!configured || !adRef.current || !onStatusChange) return undefined;
    const node = adRef.current;
    const reportStatus = () => onStatusChange(node.getAttribute("data-ad-status"));
    const observer = new MutationObserver(reportStatus);
    observer.observe(node, { attributes: true, attributeFilter: ["data-ad-status"] });
    reportStatus();
    return () => observer.disconnect();
  }, [configured, onStatusChange, slot]);
  if (!configured) return null;

  return (
    <div className={className} data-testid="adsense-slot">
      <div className="text-center text-[9px] uppercase tracking-[0.18em] text-[#7a7a80] mb-2">Advertisement</div>
      <ins
        ref={adRef}
        className="adsbygoogle block"
        data-ad-client={ADSENSE_PUBLISHER_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={fullWidthResponsive ? "true" : "false"}
        style={{ display: "block" }}
      />
    </div>
  );
}
